import Spinner from '@src/components/common/Spinner';
import { useFileUpload } from '@src/hooks/useFileUpload';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import UploadIcon from '@src/svgs/UploadIcon';
import { LogManager } from '@src/utility/Logger';
import { useState } from 'react';
import styled from 'styled-components';

import { useSaveUploadedMediaCard } from './useSaveUploadedMediaCard';

import type { CompositionTemplate } from './types';
import type { SaveUploadedMediaCardInput } from './useSaveUploadedMediaCard';
import type { UploadedMediaCardType } from '@src/services/videoTemplate/uploadedMediaCard';
import type { Artifact } from '@src/types.ts/story';

const logger = LogManager.getLogger('component:UploadMediaCardTile');

interface UploadMediaCardTileProps {
  cardType: UploadedMediaCardType;
  /** The grid uploaded from — the saved template is filed there. */
  target?: SaveUploadedMediaCardInput['target'];
  /** The Graphics panel's own add — the card lands like any design. */
  onAdd: (template: CompositionTemplate) => void;
}

/**
 * PLAT-11732 — the first tile of the Intro / Outro card grids: pick an image
 * or video and it is saved as a workspace template in this grid and added as
 * a full-frame card, the way clicking a design adds that design. A video card
 * takes the video's length.
 */
export const UploadMediaCardTile = ({
  cardType,
  target,
  onAdd,
}: UploadMediaCardTileProps) => {
  const videoCreator = useVideoCreatorStore();
  const saveUploadedMediaCard = useSaveUploadedMediaCard();
  const [isPreparing, setIsPreparing] = useState(false);

  const addUploadedCard = async (artifact: Artifact) => {
    setIsPreparing(true);
    const result = await saveUploadedMediaCard({ artifact, cardType, target });
    setIsPreparing(false);

    if (!result.ok) {
      logger.warn('Could not build a card from the upload', {
        url: artifact.url,
        error: result.error,
      });
      videoCreator.showToast({
        state: 'error',
        message: "Couldn't read this video's length. Try another file.",
      });
      return;
    }
    // Added by the generated id, which resolves at once: the new record may
    // not be in the slot's lookup until the next render.
    onAdd({ ...result.value.template, id: result.value.cardId });
  };

  const { acceptFormats, handleUploadFile, isUploading } = useFileUpload({
    acceptType: 'all',
    uploadType: 'all',
    onUploadComplete: artifacts => {
      if (artifacts[0]) void addUploadedCard(artifacts[0]);
    },
  });

  const isBusy = isUploading || isPreparing;

  return (
    <TileWrapper>
      <Tile aria-busy={isBusy || undefined} $disabled={isBusy}>
        {isBusy ? <Spinner size="sm" /> : <UploadIcon width="20" height="20" />}
        <TileText>{isBusy ? 'Uploading…' : 'Upload'}</TileText>
        <FileInput
          // remounted once an upload settles, so picking the same file again
          // still fires onChange
          key={isBusy ? 'upload-busy' : 'upload-ready'}
          type="file"
          accept={acceptFormats.join(',')}
          onChange={handleUploadFile}
          disabled={isBusy}
          aria-label="Upload Image or Video"
          title=""
        />
      </Tile>
      <TileName>Upload Image or Video</TileName>
    </TileWrapper>
  );
};

/* Sized and labelled like the design cards beside it (CompositionCard): a 16:9
   frame with its name underneath. Dashed, so it reads as a slot to fill rather
   than a design to apply. */
const TileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Tile = styled.label<{ $disabled: boolean }>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 5px;
  border: 2px dashed var(--border);
  background: var(--background);
  color: var(--text-secondary);
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  transition:
    border-color var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    border-color: ${props =>
      props.$disabled ? 'var(--border)' : 'var(--primary)'};
    color: var(--foreground);
  }
`;

const TileText = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const TileName = styled.p`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 159%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileInput = styled.input`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  &:disabled {
    display: none;
  }
`;
